import {
    Table,
    TableBody,
    TableCell,
    TableCellExpandRow,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
    TableRowExpanded,
} from '@ksyos/design-system';
import { Fragment, useMemo, useState } from 'react';
import type { IProcessQuestionnaireResponseItem } from '@common/interfaces/api/processQuestionnaireResponsesApi';
import type { IQuestionnaireItem } from '@common/interfaces/api/questionnairesApi';
import type { IHsdqOsasQuestionnaire } from '@common/interfaces/osasQuestionnaire';
import { HsdqCategoryName } from '@common/enums';
import {
    getMappedQuestionnaireResponseAnswers,
    hsdqQuestionnairePrefixMappingToKeys,
} from '@common/utils/osasQuestionnaireItemsMappingUtils';
import {
    calculateHsdqScoreForCategory,
    getHsdqQuestionsByCategory,
    hsdqCriteriums,
} from '@common/utils/osasQuestionnaireUtils';
import { QuestionnaireResponseTable } from '@frontend/components/elements/questionnaireResponse/QuestionnaireResponseTable';

interface IHsdqQuestionnaireResponseProps {
    questionnaireItems: IQuestionnaireItem[];
    questionnaireResponseItems: IProcessQuestionnaireResponseItem[];
}

interface IResponseItemsPerCategoryWithScoresItem {
    categoryName: HsdqCategoryName;
    criteria: number;
    items: IProcessQuestionnaireResponseItem[];
    overrun: 'Ja' | 'Nee';
    score: string;
}

const customTableRowStyles = {
    '&:hover td': {
        backgroundColor: '#F4F4F2 !important',
    },
};

const customTableCellStyles = {
    backgroundColor: '#FAF9F8',
    borderTop: '4px solid #fff',
};

export function HsdqQuestionnaireResponse(props: Readonly<IHsdqQuestionnaireResponseProps>) {
    const { questionnaireItems, questionnaireResponseItems } = props;
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    const responseItemsPerCategoryWithScores = useMemo(() => {
        const mappedResponseItems = getMappedQuestionnaireResponseAnswers(questionnaireResponseItems);

        const response: IResponseItemsPerCategoryWithScoresItem[] = [];

        for (const key of Object.keys(HsdqCategoryName)) {
            const categoryName: HsdqCategoryName = HsdqCategoryName[key as keyof typeof HsdqCategoryName];
            const score = calculateHsdqScoreForCategory(mappedResponseItems, categoryName) ?? 0;

            const responseItemsInCategory =
                getHsdqQuestionsByCategory(categoryName, mappedResponseItems as IHsdqOsasQuestionnaire) ?? {};

            const items = questionnaireResponseItems.filter((item) =>
                Object.keys(responseItemsInCategory).includes(
                    hsdqQuestionnairePrefixMappingToKeys[
                        item.prefix as keyof typeof hsdqQuestionnairePrefixMappingToKeys
                    ]
                )
            );

            response.push({
                categoryName: categoryName,
                criteria: hsdqCriteriums.get(categoryName)!,
                items: items,
                overrun: score > hsdqCriteriums.get(categoryName)! ? 'Ja' : 'Nee',
                score: score.toFixed(2),
            });
        }

        return response;
    }, [questionnaireResponseItems]);

    return (
        <TableContainer>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableHeader></TableHeader>
                        <TableHeader></TableHeader>
                        <TableHeader>Score</TableHeader>
                        <TableHeader>Overschrijding</TableHeader>
                        <TableHeader>Criterium</TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {responseItemsPerCategoryWithScores.map((item) => {
                        const isExpanded = expandedRows.includes(item.categoryName);

                        return (
                            <Fragment key={item.categoryName}>
                                <TableRow
                                    key={item.categoryName}
                                    sx={{ ...customTableRowStyles }}
                                >
                                    <TableCellExpandRow
                                        isExpandable
                                        isExpanded={isExpanded}
                                        onExpand={() => handleExpand(item.categoryName)}
                                        sx={{ ...customTableCellStyles }}
                                    />

                                    <TableCell
                                        sx={{
                                            fontWeight: 'medium',
                                            ...customTableCellStyles,
                                        }}
                                    >
                                        {item.categoryName}
                                    </TableCell>
                                    <TableCell sx={{ ...customTableCellStyles }}>{item.score}</TableCell>
                                    <TableCell sx={{ ...customTableCellStyles }}>{item.overrun}</TableCell>
                                    <TableCell sx={{ ...customTableCellStyles }}>{item.criteria}</TableCell>
                                </TableRow>

                                {expandedRows.includes(item.categoryName) && (
                                    <TableRowExpanded isExpanded={isExpanded}>
                                        <TableCell
                                            colSpan={5}
                                            sx={{ p: 0 }}
                                        >
                                            {renderItems(item.items)}
                                        </TableCell>
                                    </TableRowExpanded>
                                )}
                            </Fragment>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );

    function handleExpand(rowId: string) {
        const newExpandedRows = expandedRows.slice();
        const index = newExpandedRows.indexOf(rowId);
        if (index >= 0) {
            newExpandedRows.splice(index, 1);
        } else {
            newExpandedRows.push(rowId);
        }
        setExpandedRows(newExpandedRows);
    }

    function renderItems(items: IProcessQuestionnaireResponseItem[]) {
        if (!items) {
            return null;
        }

        return (
            <QuestionnaireResponseTable
                questionnaireItems={questionnaireItems}
                questionnaireResponseItems={items}
            />
        );
    }
}
